import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import InputCustom from "../../../../../../components/InputCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { FrontendNotification } from "../../../../../../shared/Notification";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";
import { ITipoCargaSPE } from "../../types/types";
import * as Yup from "yup";

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: ITipoCargaSPE;
  onConfirm: () => void;
  onClose: () => void;
}

interface FormValues {
  tipo_carga: string;
}

const formValidator = Yup.object().shape({
  tipo_carga: Yup.string().required("Este campo é obrigatório!"),
});

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        setLoading(true);

        const body: any = {
          tipo_carga: values.tipo_carga,
          id_usuario_historico: user?.id,
        };

        if (props.isEdit && props.selectedRow?.id_tipo_carga_SPE) {
          body.id_tipo_carga_SPE = props.selectedRow.id_tipo_carga_SPE;
          const response = await api.post("/editar/tipoCargaSpe", body).catch(() =>
            api.post("/atualizar/tipoCargaSpe", body)
          );
          if (response.status === 200 || response.data) {
            FrontendNotification("Tipo de carga SPE atualizado com sucesso!", "success");
            props.onConfirm();
          } else {
            FrontendNotification("Erro ao atualizar o tipo de carga SPE!", "error");
          }
        } else {
          const response = await api.post("/cadastrar/tipoCargaSpe", body);
          if (response.status === 200 || response.data) {
            FrontendNotification("Tipo de carga SPE cadastrado com sucesso!", "success");
            props.onConfirm();
          } else {
            FrontendNotification("Erro ao cadastrar o tipo de carga SPE!", "error");
          }
        }

        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        const msg = error?.response?.data?.error_msg || "Ocorreu um erro ao salvar o tipo de carga SPE.";
        FrontendNotification(msg, "error");
      }
    },
    [props, user]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      tipo_carga: "",
    },
    validationSchema: formValidator,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (props.selectedRow && (props.isView || props.isEdit)) {
      formik.setFieldValue("tipo_carga", props.selectedRow.tipo_carga || "");
    }
  }, [props.selectedRow, props.isView, props.isEdit]);

  return (
    <>
      <Loading loading={loading} skeletonFields={1} skeletonColumns={1} />
      <div className="overflow-y-auto max-h-[calc(70vh)] p-5">
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div>
            <InputCustom
              id="tipo_carga"
              title="Tipo de Carga"
              placeholder="Digite o tipo de carga"
              onChange={formik.handleChange("tipo_carga")}
              value={formik.values.tipo_carga}
              touched={formik.touched.tipo_carga}
              error={formik.errors.tipo_carga}
              disabled={props.isView}
            />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        {!props.isView && (
          <button
            type="button"
            className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
            onClick={() => formik.handleSubmit()}
            id="btnSave"
          >
            Salvar
          </button>
        )}
      </div>
    </>
  );
};

export default Form;
